import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { can, localIsoDate, nextQuoteNumber } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  activeClauses,
  createQuote,
  listQuotes,
  loadTenantDocuments,
  quoteNumbers,
  type QuoteListRow,
  type TenantDocuments,
} from './api.ts';
import { QuoteEditor } from './QuoteEditor.tsx';
import { QUOTE_STATUS_LABEL, tenantQcDefaults } from './shared.ts';
import { TableWrap } from '../components/TableWrap.tsx';

export interface QuotesScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  /** Open a contract made from a quote (Contracts page). */
  readonly onOpenContract: (contractId: string) => void;
}

/** Quotes list and the guided editor (help `cloud.quotes`). */
export function QuotesScreen({
  sb,
  tenantId,
  userId,
  roles,
  onOpenContract,
}: QuotesScreenProps): ReactElement {
  const [rows, setRows] = useState<QuoteListRow[] | null>(null);
  const [docs, setDocs] = useState<TenantDocuments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const write = can(roles, 'quote.draft');

  const reload = useCallback(() => {
    setError(null);
    Promise.all([listQuotes(sb, tenantId), loadTenantDocuments(sb, tenantId)])
      .then(([r, d]) => {
        setRows(r);
        setDocs(d);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  if (open && docs) {
    return (
      <QuoteEditor
        sb={sb}
        tenantId={tenantId}
        userId={userId}
        roles={roles}
        quoteId={open}
        docs={docs}
        onBack={() => {
          setOpen(null);
          reload();
        }}
        onOpenContract={onOpenContract}
      />
    );
  }

  async function newQuote(): Promise<void> {
    if (!docs) return;
    setBusy(true);
    setNotice(null);
    try {
      const today = localIsoDate();
      const number = nextQuoteNumber(await quoteNumbers(sb, tenantId), today);
      const params = activeClauses(docs, 'contract')?.template.params ?? {};
      const id = await createQuote(sb, tenantId, userId, number, tenantQcDefaults(docs), params);
      setOpen(id);
    } catch (e: unknown) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page">
      <PageHeader
        title="Quotes"
        subtitle="Survey proposals with their areas, specifications, QC limits and prices."
      >
        {write && (
          <Button variant="primary" busy={busy} onClick={() => void newQuote()} disabled={!docs}>
            {busy ? 'Creating quote…' : 'New quote'}
          </Button>
        )}
      </PageHeader>
      {notice && (
        <p className="notice notice--fail" role="alert">
          {notice}
        </p>
      )}
      {!write && rows && (
        <p className="body muted">
          You can open quotes and see their prices. Only managers, tenant admins and office staff change them.
        </p>
      )}
      {write && !can(roles, 'quote.send') && rows && (
        <p className="body muted">
          You can start quotes and edit drafts. A manager or tenant admin sends them to the client.
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="quotes" />}
      {rows && rows.length === 0 && (
        <div className="panel">
          <p className="body">No quotes yet.{write ? ' Press "New quote" to start one.' : ''}</p>
        </div>
      )}
      {rows && rows.length > 0 && (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th className="label">Quote</th>
                <th className="label">Client</th>
                <th className="label">Project</th>
                <th className="label">Date</th>
                <th className="label">Revision</th>
                <th className="label">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.id}>
                  <td>
                    <button type="button" className="link data" onClick={() => setOpen(q.id)}>
                      {q.number}
                    </button>
                  </td>
                  <td>{q.client_name || '–'}</td>
                  <td>{q.project_name || '–'}</td>
                  <td className="data">{q.quote_date}</td>
                  <td className="data">{q.revision}</td>
                  <td>
                    <span className="chip caption">{QUOTE_STATUS_LABEL[q.status]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}
